import apiUrls from "../../../api";
import trackEvent from "../../../trackEvent";
import { AUTH_JWT_KEY } from "../../../common/util/localStorage";
import type { EventSurface } from "../registry/types";

export type OnboardingEventName =
  | "tour_started"
  | "tour_step_viewed"
  | "tour_completed"
  | "tour_skipped"
  | "tour_target_missing"
  | "tour_paused"
  | "tour_resumed"
  | "welcome_shown"
  | "welcome_dismissed"
  | "welcome_cta"
  | "checklist_item_done"
  | "checklist_dismissed"
  | "checklist_restored"
  | "management_replay"
  | "reset_all"
  | "sample_course_cloned"
  | "relaunch_shown"
  | "relaunch_accepted"
  | "relaunch_declined"
  | "suggestions_muted"
  | "suggestions_unmuted";

export type OnboardingEventMeta = Record<string, string | number | boolean>;

export type OnboardingEventInput = {
  event: OnboardingEventName;
  surface: EventSurface;
  itemId?: string | null;
  step?: number | null;
  meta?: OnboardingEventMeta;
};

const FLUSH_DELAY_MS = 5000;
const FLUSH_AT_COUNT = 20;
const FLUSH_BATCH_SIZE = 50;

const queue: OnboardingEventInput[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerRegistered = false;

export const flushOnboardingEvents = async (): Promise<void> => {
  if (queue.length === 0) {
    return;
  }

  const jwt = localStorage.getItem(AUTH_JWT_KEY);
  if (!jwt) {
    queue.length = 0;
    return;
  }

  const batch = queue.splice(0, FLUSH_BATCH_SIZE);

  try {
    await fetch(apiUrls.onboarding.events, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ events: batch }),
    });
  } catch {
    return;
  }
};

const scheduleFlush = (): void => {
  if (flushTimer) {
    return;
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushOnboardingEvents();
  }, FLUSH_DELAY_MS);
};

export const enqueueOnboardingEvent = (input: OnboardingEventInput): void => {
  queue.push(input);
  trackEvent(`onboarding-${input.event.replace(/_/g, "-")}`, {
    surface: input.surface,
    itemId: input.itemId ?? null,
    step: input.step ?? null,
  });

  if (queue.length >= FLUSH_AT_COUNT) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    void flushOnboardingEvents();
    return;
  }

  scheduleFlush();
};

export const clearOnboardingEventQueue = (): void => {
  queue.length = 0;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};

const registerVisibilityListener = (): void => {
  if (visibilityListenerRegistered || typeof document === "undefined") {
    return;
  }
  visibilityListenerRegistered = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushOnboardingEvents();
    }
  });
};

registerVisibilityListener();
